import { MGPFallible, MGPOptional, MGPValidation } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { CoordFailure } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { MGPValidators } from '../../utils/MGPValidator';
import { EpaminondasFailure } from './EpaminondasFailure';
import { EpaminondasState } from './EpaminondasState';
export class EpaminondasNode extends GameNode {
}
export class EpaminondasRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Epaminondas`,
        config: {
            width: new NumberConfig(14, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(1, 99)),
            emptyRows: new NumberConfig(8, RulesConfigDescriptionLocalizable.NUMBER_OF_EMPTY_ROWS, MGPValidators.range(1, 99)),
            rowsOfSoldiers: new NumberConfig(2, RulesConfigDescriptionLocalizable.NUMBER_OF_PIECES_ROWS, MGPValidators.range(1, 99)),
        },
    });
    static get() {
        if (EpaminondasRules.singleton.isAbsent()) {
            EpaminondasRules.singleton = MGPOptional.of(new EpaminondasRules());
        }
        return EpaminondasRules.singleton.get();
    }
    static isLegal(move, state) {
        const phalanxValidity = this.getPhalanxValidity(state, move);
        if (phalanxValidity.isFailure()) {
            return MGPFallible.failure(phalanxValidity.getReason());
        }
        const landingStatus = this.getLandingStatus(state, move);
        if (landingStatus.isFailure()) {
            return landingStatus;
        }
        const newBoard = TableUtils.copy(landingStatus.get());
        const opponent = state.getCurrentOpponent();
        const captureValidity = EpaminondasRules.getCaptureValidity(state, newBoard, move, opponent);
        if (captureValidity.isFailure()) {
            return captureValidity;
        }
        return MGPFallible.success(captureValidity.get());
    }
    static getPhalanxValidity(state, move) {
        let coord = move.coord;
        if (state.isNotOnBoard(coord)) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(coord));
        }
        const opponent = state.getCurrentOpponent();
        for (let soldierIndex = 0; soldierIndex < move.phalanxSize; soldierIndex++) {
            if (state.isNotOnBoard(coord)) {
                return MGPValidation.failure(EpaminondasFailure.PHALANX_CANNOT_CONTAIN_PIECES_OUTSIDE_BOARD());
            }
            const spaceContent = state.getPieceAt(coord);
            if (spaceContent.isNone()) {
                return MGPValidation.failure(EpaminondasFailure.PHALANX_CANNOT_CONTAIN_EMPTY_SQUARE());
            }
            if (spaceContent === opponent) {
                return MGPValidation.failure(EpaminondasFailure.PHALANX_CANNOT_CONTAIN_OPPONENT_PIECE());
            }
            coord = coord.getNext(move.direction, 1);
        }
        return MGPValidation.SUCCESS;
    }
    static getLandingStatus(state, move) {
        const newBoard = state.getCopiedBoard();
        const currentPlayer = state.getCurrentPlayer();
        let emptied = move.coord;
        let landingCoord = move.coord.getNext(move.direction, move.phalanxSize);
        let landingIndex = 0;
        while (landingIndex + 1 < move.stepSize) {
            newBoard[emptied.y][emptied.x] = PlayerOrNone.NONE;
            newBoard[landingCoord.y][landingCoord.x] = currentPlayer;
            if (state.isNotOnBoard(landingCoord)) {
                return MGPFallible.failure(EpaminondasFailure.PHALANX_IS_LEAVING_BOARD());
            }
            if (state.getPieceAt(landingCoord).isPlayer()) {
                return MGPFallible.failure(EpaminondasFailure.SOMETHING_IN_PHALANX_WAY());
            }
            landingIndex++;
            landingCoord = landingCoord.getNext(move.direction, 1);
            emptied = emptied.getNext(move.direction, 1);
        }
        if (state.isNotOnBoard(landingCoord)) {
            return MGPFallible.failure(EpaminondasFailure.PHALANX_IS_LEAVING_BOARD());
        }
        if (state.getPieceAt(landingCoord) === currentPlayer) {
            return MGPFallible.failure(RulesFailure.SHOULD_LAND_ON_EMPTY_OR_OPPONENT_SPACE());
        }
        newBoard[emptied.y][emptied.x] = PlayerOrNone.NONE;
        newBoard[landingCoord.y][landingCoord.x] = currentPlayer;
        return MGPFallible.success(newBoard);
    }
    static getCaptureValidity(oldState, board, move, opponent) {
        let capturedSoldier = move.coord.getNext(move.direction, move.phalanxSize + move.stepSize - 1);
        let captured = 0;
        while (oldState.hasPieceAt(capturedSoldier, opponent)) {
            // Capture
            if (captured > 0) {
                board[capturedSoldier.y][capturedSoldier.x] = PlayerOrNone.NONE;
            }
            captured++;
            if (move.phalanxSize <= captured) {
                return MGPFallible.failure(EpaminondasFailure.PHALANX_SHOULD_BE_GREATER_TO_CAPTURE());
            }
            capturedSoldier = capturedSoldier.getNext(move.direction, 1);
        }
        return MGPFallible.success(board);
    }
    getInitialState(config) {
        const _ = PlayerOrNone.NONE;
        const O = PlayerOrNone.ZERO;
        const X = PlayerOrNone.ONE;
        const upperBoard = TableUtils.create(config.width, config.rowsOfSoldiers, X);
        const middleBoard = TableUtils.create(config.width, config.emptyRows, _);
        const lowerBoard = TableUtils.create(config.width, config.rowsOfSoldiers, O);
        const board = upperBoard.concat(middleBoard).concat(lowerBoard);
        return new EpaminondasState(board, 0);
    }
    getRulesConfigDescription() {
        return EpaminondasRules.RULES_CONFIG_DESCRIPTION;
    }
    isLegal(move, state) {
        return EpaminondasRules.isLegal(move, state);
    }
    applyLegalMove(_move, state, _config, newBoard) {
        const resultingState = new EpaminondasState(newBoard, state.turn + 1);
        return resultingState;
    }
    getGameStatus(node, _config) {
        const state = node.gameState;
        const zerosInFirstLine = state.countPieceInRow(Player.ZERO, 0);
        const height = state.getHeight();
        const onesInLastLine = state.countPieceInRow(Player.ONE, height - 1);
        if (state.turn % 2 === 0) {
            if (zerosInFirstLine > onesInLastLine) {
                return GameStatus.ZERO_WON;
            }
        }
        else {
            if (onesInLastLine > zerosInFirstLine) {
                return GameStatus.ONE_WON;
            }
        }
        const doesZeroOwnPieces = state.doesOwnPiece(Player.ZERO);
        if (doesZeroOwnPieces === false) {
            return GameStatus.ONE_WON;
        }
        const doesOneOwnPieces = state.doesOwnPiece(Player.ONE);
        if (doesOneOwnPieces === false) {
            return GameStatus.ZERO_WON;
        }
        return GameStatus.ONGOING;
    }
}
